import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RideWiseRow, VehicleRow } from "./types";
import { fmtMoney, fmtNum, safeArr } from "./helpers";

interface Props {
  rw: RideWiseRow[];
  vehiclesById: Map<string, VehicleRow>;
  loading: boolean;
}

interface Agg {
  vehicle_identifier: string;
  vehicle_type: string;
  status: string;
  jobs: number;
  passengers: number;
  fare_total: number;
  subsidy: number;
  km: number;
  hoist: number;
  days: Set<string>;
  lastTrip: string;
}

const VehiclesTab: React.FC<Props> = ({ rw, vehiclesById, loading }) => {
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const map = new Map<string, Agg>();
    for (const r of safeArr(rw)) {
      const key = String(r.vehicle_identifier || "—");
      const veh = vehiclesById.get(key);
      const cur =
        map.get(key) ||
        ({
          vehicle_identifier: key,
          vehicle_type: veh?.vehicle_type || "",
          status: veh
            ? (veh.status || "Active")
            : "Historical / Not in vehicle list",
          jobs: 0, passengers: 0, fare_total: 0, subsidy: 0, km: 0, hoist: 0,
          days: new Set<string>(), lastTrip: "",
        } as Agg);
      cur.jobs += 1;
      cur.passengers += Number(r.number_of_passengers || 0);
      cur.fare_total += Number(r.fare_total || 0);
      cur.subsidy += Number(r.concession_amount || 0);
      cur.km += Number(r.kilometres || 0);
      if (r.hoist_deployed) cur.hoist += 1;
      if (r.trip_date) {
        cur.days.add(r.trip_date);
        if (r.trip_date > cur.lastTrip) cur.lastTrip = r.trip_date;
      }
      map.set(key, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.jobs - a.jobs);
  }, [rw, vehiclesById]);

  const filtered = rows.filter((r) =>
    !q.trim() ||
    r.vehicle_identifier.toLowerCase().includes(q.toLowerCase()) ||
    (r.vehicle_type || "").toLowerCase().includes(q.toLowerCase())
  );

  const overall = useMemo(() => {
    const totalJobs = rows.reduce((s, r) => s + r.jobs, 0);
    const avg = rows.length ? totalJobs / rows.length : 0;
    return { avg };
  }, [rows]);

  if (loading) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">Loading vehicles…</CardContent>
      </Card>
    );
  }

  if (!rows.length) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">No vehicle data in period.</CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Vehicles ({fmtNum(rows.length)})</CardTitle>
        <Input
          placeholder="Search vehicle…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="dark-input max-w-xs"
        />
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-muted-foreground border-b border-border">
            <tr>
              <th className="text-left p-2">Vehicle</th>
              <th className="text-left p-2">Type</th>
              <th className="text-left p-2">Status</th>
              <th className="text-right p-2">Jobs</th>
              <th className="text-right p-2">Pax</th>
              <th className="text-right p-2">Fare</th>
              <th className="text-right p-2">Subsidy</th>
              <th className="text-right p-2">KM</th>
              <th className="text-right p-2">Hoist</th>
              <th className="text-right p-2">Days</th>
              <th className="text-right p-2">Jobs/Day</th>
              <th className="text-right p-2">$/Job</th>
              <th className="text-right p-2">KM/Job</th>
              <th className="text-left p-2">Last Trip</th>
              <th className="text-left p-2">Tags</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const days = r.days.size || 1;
              const jpd = r.jobs / days;
              const valpj = r.fare_total / (r.jobs || 1);
              const kmpj = r.km / (r.jobs || 1);
              const hoistShare = r.jobs ? r.hoist / r.jobs : 0;
              const isHistorical = r.status.toLowerCase().includes("historical") || r.status.toLowerCase() === "inactive";
              return (
                <tr key={r.vehicle_identifier} className="border-b border-border/50">
                  <td className="p-2 font-mono">{r.vehicle_identifier}</td>
                  <td className="p-2">{r.vehicle_type || "—"}</td>
                  <td className="p-2">
                    <Badge variant={isHistorical ? "secondary" : "default"}>{r.status}</Badge>
                  </td>
                  <td className="p-2 text-right">{fmtNum(r.jobs)}</td>
                  <td className="p-2 text-right">{fmtNum(r.passengers)}</td>
                  <td className="p-2 text-right">{fmtMoney(r.fare_total)}</td>
                  <td className="p-2 text-right">{fmtMoney(r.subsidy)}</td>
                  <td className="p-2 text-right">{fmtNum(r.km, 1)}</td>
                  <td className="p-2 text-right">{fmtNum(r.hoist)}</td>
                  <td className="p-2 text-right">{fmtNum(days)}</td>
                  <td className="p-2 text-right">{fmtNum(jpd, 1)}</td>
                  <td className="p-2 text-right">{fmtMoney(valpj)}</td>
                  <td className="p-2 text-right">{fmtNum(kmpj, 1)}</td>
                  <td className="p-2">{r.lastTrip || "—"}</td>
                  <td className="p-2 space-x-1">
                    {!isHistorical && <Badge className="bg-green-700 text-white">Active</Badge>}
                    {isHistorical && <Badge variant="secondary">Historical</Badge>}
                    {hoistShare > 0.4 && <Badge className="bg-purple-700 text-white">Hoist Heavy</Badge>}
                    {jpd > overall.avg * 1.5 && <Badge className="bg-blue-700 text-white">High Use</Badge>}
                    {jpd < overall.avg * 0.5 && <Badge className="bg-orange-700 text-white">Underused</Badge>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

export default VehiclesTab;
